/**
 * Magasin `sales` : les ventes enregistrees sur le poste (plan 1.2 et 1.6,
 * PR 1.5b). Un enregistrement est une VenteEnregistree du contrat
 * (vente/contrat.js), cle sale_uuid.
 *
 * Enregistrement ATOMIQUE (plan 1.6) : UNE transaction IndexedDB couvre la
 * vente, le compteur des numeros locaux (meta) et le controle du quart
 * ouvert (shifts). Si une ecriture echoue, la transaction est annulee : ni
 * vente, ni numero consomme. Le journal et la file d'envoi (PR 2.1 et 2.3)
 * entreront dans la meme transaction.
 *
 * Idempotence : un deuxieme envoi du meme sale_uuid (double clic, retour a la
 * vente puis nouvel encaissement) rend la vente deja ecrite, sans en creer
 * une autre ni consommer de numero. IndexedDB execute une a la fois les
 * transactions d'ecriture qui touchent les memes magasins, meme entre deux
 * onglets : la lecture puis l'ajout ne peuvent pas se croiser.
 */
import { requestToPromise, transactionDone } from './idb';
import { META_KEYS, STORES } from './schema';
import { SHIFT_STATUS, horodatageLocal } from './shifts';
import { paiementComptant } from '../domain/cashRounding';
import { verifierPanier } from '../vente/contrat';

export const SALE_STATUS = Object.freeze({ COMPLETED: 'completed' });

export class SaleError extends Error {
    constructor(message, code, details = null) {
        super(message);
        this.name = 'SaleError';
        this.code = code;
        if (details) this.details = details;
    }
}

/**
 * Numero local de la vente : magasin, poste, compteur sur 6 chiffres
 * (S03-P1-000123). Le poste est le terminal_id de l'ERP, unique entre tous
 * les magasins : deux postes n'ont jamais le meme numero.
 */
export function formatNumeroLocal(storeId, terminalId, n) {
    return `S${String(storeId).padStart(2, '0')}-P${terminalId}-${String(n).padStart(6, '0')}`;
}

/**
 * Enregistre la vente payee comptant.
 *
 * @param {IDBDatabase} db
 * @param {object} options
 * @param {import('../vente/contrat').PanierAEncaisser} options.panier recu tel quel de l'ecran de vente
 * @param {string} options.shiftId quart ouvert
 * @param {number} options.cashReceivedCents montant recu, >= montant du
 * @param {() => Date} [options.now]
 * @returns {Promise<{ vente: object, created: boolean }>}
 */
export async function recordSale(db, { panier, shiftId, cashReceivedCents, now = () => new Date() }) {
    const erreurs = verifierPanier(panier);
    if (erreurs.length > 0) throw new SaleError('Panier invalide', 'PANIER_INVALIDE', erreurs);
    let payment;
    try {
        payment = paiementComptant(panier.totals.total_cents, cashReceivedCents);
    } catch (error) {
        throw new SaleError('Paiement incomplet', 'PAIEMENT_INCOMPLET', [error.message]);
    }
    if (typeof shiftId !== 'string' || shiftId === '') throw new SaleError('Aucun quart ouvert', 'QUART_FERME');

    const tx = db.transaction([STORES.SALES, STORES.META, STORES.SHIFTS], 'readwrite');
    const done = transactionDone(tx);
    const sales = tx.objectStore(STORES.SALES);
    const meta = tx.objectStore(STORES.META);
    try {
        const existing = await requestToPromise(sales.get(panier.sale_uuid));
        if (existing) {
            await done;
            return { vente: existing, created: false };
        }

        const terminalRecord = await requestToPromise(meta.get(META_KEYS.TERMINAL));
        const terminal = terminalRecord && terminalRecord.value;
        if (!terminal || terminal.terminal_id !== panier.terminal_id || terminal.store_id !== panier.store_id) {
            throw new SaleError('Panier d\'un autre poste', 'POSTE_DIFFERENT');
        }

        const pointer = await requestToPromise(meta.get(META_KEYS.OPEN_SHIFT));
        const shift = await requestToPromise(tx.objectStore(STORES.SHIFTS).get(shiftId));
        if (!pointer || pointer.value !== shiftId || !shift || shift.status !== SHIFT_STATUS.OPEN) {
            throw new SaleError('Aucun quart ouvert', 'QUART_FERME');
        }

        const counter = await requestToPromise(meta.get(META_KEYS.SALE_COUNTER));
        const last = counter && Number.isSafeInteger(counter.value && counter.value.last) ? counter.value.last : 0;
        const next = last + 1;

        const vente = {
            ...panier,
            shift_id: shiftId,
            local_number: formatNumeroLocal(terminal.store_id, terminal.terminal_id, next),
            payment,
            status: SALE_STATUS.COMPLETED,
            completed_at: horodatageLocal(now()),
            journal_seq: null,
            receipt_print_count: 0,
        };
        meta.put({ key: META_KEYS.SALE_COUNTER, value: { last: next } });
        // add() et non put() : une vente deja ecrite n'est jamais remplacee.
        sales.add(vente);
        await done;
        return { vente, created: true };
    } catch (error) {
        // Une ecriture qui leve (valeur non clonable...) laisserait valider
        // les precedentes a la fin de la transaction : on l'annule.
        done.catch(() => {});
        try { tx.abort(); } catch (_alreadyFinished) { /* transaction deja terminee */ }
        throw error;
    }
}

/** Vente enregistree, ou null. */
export async function getSale(db, saleUuid) {
    const tx = db.transaction(STORES.SALES, 'readonly');
    return (await requestToPromise(tx.objectStore(STORES.SALES).get(saleUuid))) || null;
}

/** Ventes d'un quart (index shift_id). */
export async function listSalesByShift(db, shiftId) {
    const tx = db.transaction(STORES.SALES, 'readonly');
    return requestToPromise(tx.objectStore(STORES.SALES).index('shift_id').getAll(shiftId));
}

/**
 * Note une impression du recu (premiere ou reimpression : +1). Rien d'autre
 * de la vente ne change.
 * @returns {Promise<object>} la vente a jour
 */
export async function marquerRecuImprime(db, saleUuid) {
    const tx = db.transaction(STORES.SALES, 'readwrite');
    const done = transactionDone(tx);
    const sales = tx.objectStore(STORES.SALES);
    try {
        const vente = await requestToPromise(sales.get(saleUuid));
        if (!vente) throw new SaleError('Vente introuvable', 'VENTE_INTROUVABLE');
        const next = { ...vente, receipt_print_count: (vente.receipt_print_count || 0) + 1 };
        sales.put(next);
        await done;
        return next;
    } catch (error) {
        done.catch(() => {});
        try { tx.abort(); } catch (_alreadyFinished) { /* transaction deja terminee */ }
        throw error;
    }
}
